// mds-dojo — dojo/date/locale. WIDENED (additive) to carry the faithful CLDR
// pattern engine ported from references/dojo/date/locale.js (BSD-3, © JS Foundation),
// so the dijit DATE/TIME text-box chain (DateTextBox / TimeTextBox / _DateTimeTextBox /
// _TimePicker) can format/parse/validate via CLDR patterns exactly as real dojo does.
//
// FAITHFUL SURFACE added alongside the pre-existing exports:
//   _processPattern, formatPattern (private), _buildDateTimeRE (private),
//   _parseInfo, regexp, _getGregorianBundle, addCustomFormats, _getWeekOfYear,
//   _getDayOfYear, _getZone, isWeekend. `format`/`parse` now route the CLDR-pattern
//   path through this engine.
//
// The old exports keep working: getNames / getFirstDayOfWeek are untouched; format()
// still honours the app's `{datePattern[,timePattern],selector}` calls (and the pinned
// date-locale.test) via the EXPLICIT-pattern branch below.
//
// FAITHFUL ADAPTATIONS (flagged):
//  - dojo loaded CLDR gregorian data + number symbols via `dojo/i18n!../cldr/nls/gregorian`.
//    mds has no runtime i18n loader, so the gregorian *pattern* data (dateFormat-* /
//    timeFormat-* / dateTimeFormat-* / am-pm / eras) is INLINED below for `en` + `de`
//    (with `en` as the fallback), and the localized month/day *name* arrays are sourced
//    from getNames() (Intl-backed, exactly how the rest of this module resolves names).
//  - This module's parse/format operate on ASCII digits only (dojo's _buildDateTimeRE and
//    formatPattern do too — they never consult CLDR number symbols), so NO number-symbol
//    table is needed here; dojo/number owns that.
//  - addCustomFormats() keeps its faithful signature but, lacking an i18n loader, can only
//    merge a directly-supplied bundle object (not a package+bundleName reference).
//  - `date.getTimezoneName` (used by _getZone for z/Z/v tokens) is not on mds dojo/date, so
//    a faithful local getTimezoneName() port lives here.

import supplemental from '../cldr/supplemental';
import array from '../_base/array';
import lang from '../_base/lang';
import dregexp from '../regexp';
import string from '../string';
import ddate from '../date';

function pad2(n: number): string { return (n < 10 ? '0' : '') + n; }

// App-active locale (window.currentLanguageCode2, e.g. "de"), then options.locale, then the
// browser default. Used to localize the E/M name tokens and to pick the CLDR bundle.
function resolveLocale(options: any): string {
  if (options && options.locale) { return String(options.locale); }
  if (typeof window !== 'undefined' && (window as any).currentLanguageCode2) {
    return String((window as any).currentLanguageCode2);
  }
  return (typeof navigator !== 'undefined' && navigator.language) || 'en';
}

// Faithful analogue of dojo/i18n.normalizeLocale: lower-case, default to the app locale.
function normalizeLocale(locale?: string): string {
  return locale ? String(locale).toLowerCase() : String(resolveLocale({})).toLowerCase();
}

// ---------------------------------------------------------------------------
// Inlined CLDR gregorian *pattern* data (replaces dojo/i18n!../cldr/nls/gregorian).
// Month/day NAME arrays are NOT stored here — they come from getNames() at bundle-build
// time so they track Intl/browser CLDR like everything else in this module.
// ---------------------------------------------------------------------------
const _cldrPatterns: any = {
  en: {
    'dateFormat-full': 'EEEE, MMMM d, y',
    'dateFormat-long': 'MMMM d, y',
    'dateFormat-medium': 'MMM d, y',
    'dateFormat-short': 'M/d/yy',
    'timeFormat-full': 'h:mm:ss a zzzz',
    'timeFormat-long': 'h:mm:ss a z',
    'timeFormat-medium': 'h:mm:ss a',
    'timeFormat-short': 'h:mm a',
    'dateTimeFormat-full': "{1} 'at' {0}",
    'dateTimeFormat-long': "{1} 'at' {0}",
    'dateTimeFormat-medium': '{1}, {0}',
    'dateTimeFormat-short': '{1}, {0}',
    'dateFormatItem-yyyy': 'y',
    'dayPeriods-format-wide-am': 'AM',
    'dayPeriods-format-wide-pm': 'PM',
    'am': 'AM',
    'pm': 'PM',
    'eraAbbr': ['BC', 'AD'],
    'eraNames': ['Before Christ', 'Anno Domini'],
  },
  de: {
    'dateFormat-full': 'EEEE, d. MMMM y',
    'dateFormat-long': 'd. MMMM y',
    'dateFormat-medium': 'dd.MM.y',
    'dateFormat-short': 'dd.MM.yy',
    'timeFormat-full': 'HH:mm:ss zzzz',
    'timeFormat-long': 'HH:mm:ss z',
    'timeFormat-medium': 'HH:mm:ss',
    'timeFormat-short': 'HH:mm',
    'dateTimeFormat-full': "{1} 'um' {0}",
    'dateTimeFormat-long': "{1} 'um' {0}",
    'dateTimeFormat-medium': '{1}, {0}',
    'dateTimeFormat-short': '{1}, {0}',
    'dateFormatItem-yyyy': 'y',
    'dayPeriods-format-wide-am': 'AM',
    'dayPeriods-format-wide-pm': 'PM',
    'am': 'AM',
    'pm': 'PM',
    'eraAbbr': ['v. Chr.', 'n. Chr.'],
    'eraNames': ['v. Chr.', 'n. Chr.'],
  },
};

const _widthList = ['abbr', 'wide', 'narrow'];

// ---------------------------------------------------------------------------
// Bundle assembly — faithful _getGregorianBundle / addCustomFormats hookup.
// ---------------------------------------------------------------------------
const _customFormats: any[] = [];
let _cachedGregorianBundles: any = {};

export function addCustomFormats(bundleOrPkg?: any /* packageName */, _bundleName?: string): void {
  // summary:
  //		Add a reference to a bundle containing localized custom formats.
  // [ADAPTATION] Without a runtime i18n loader, only a directly-supplied bundle OBJECT can
  // be merged (the faithful (packageName, bundleName) form needs i18n.getLocalization).
  if (bundleOrPkg && typeof bundleOrPkg === 'object') {
    _customFormats.push(bundleOrPkg);
  }
  _cachedGregorianBundles = {};
}

export function _getGregorianBundle(locale: string): any {
  if (_cachedGregorianBundles[locale]) { return _cachedGregorianBundles[locale]; }
  const norm = normalizeLocale(locale);
  const base = _cldrPatterns[norm] || _cldrPatterns[norm.split('-')[0]] || _cldrPatterns.en;
  const bundle: any = lang.mixin({}, base);

  // Localized month/day NAME arrays via getNames() (Intl-backed). Intl has no separate
  // stand-alone data for en/de, so standAlone === format (dojo falls back the same way).
  array.forEach(['format', 'standAlone'], function (ctx: string) {
    array.forEach(_widthList, function (w: string) {
      bundle['days-' + ctx + '-' + w] = getNames('days', w as any, ctx, locale);
      bundle['months-' + ctx + '-' + w] = getNames('months', w as any, ctx, locale);
    });
  });

  array.forEach(_customFormats, function (b: any) { lang.mixin(bundle, b); });

  return _cachedGregorianBundles[locale] = bundle;
}

// ---------------------------------------------------------------------------
// Timezone helpers (faithful port of dojo/date.getTimezoneName + locale._getZone).
// ---------------------------------------------------------------------------
function getTimezoneName(dateObject: any): string {
  var str = dateObject.toString(), tz = '', match: any;
  var pos = str.indexOf('(');
  if (pos > -1) {
    tz = str.substring(++pos, str.indexOf(')'));
  } else {
    var pattern = /([A-Z\/]+) \d{4}/;
    if ((match = str.match(pattern))) { tz = match[1]; }
    else {
      str = dateObject.toLocaleString();
      pattern = / ([A-Z\/]+)$/;
      if ((match = str.match(pattern))) { tz = match[1]; }
    }
  }
  return (tz == 'AM' || tz == 'PM') ? '' : tz;
}

export function _getZone(dateObject: any, getName: boolean, _options?: any): any {
  // summary:
  //		Returns the zone (or offset) for the given date and options.
  if (getName) {
    return getTimezoneName(dateObject);
  } else {
    return dateObject.getTimezoneOffset();
  }
}

// ---------------------------------------------------------------------------
// formatPattern — faithful port. Format a single (literal-free) CLDR pattern.
// ---------------------------------------------------------------------------
function formatPattern(dateObject: any, bundle: any, options: any, pattern: string): string {
  return pattern.replace(/([a-z])\1*/ig, function (match: string) {
    var s: any, pad: any,
      c = match.charAt(0),
      l = match.length,
      widthList = ['abbr', 'wide', 'narrow'];
    switch (c) {
      case 'G':
        s = bundle[(l < 4) ? 'eraAbbr' : 'eraNames'][dateObject.getFullYear() < 0 ? 0 : 1];
        break;
      case 'y':
        s = dateObject.getFullYear();
        switch (l) {
          case 1:
            break;
          case 2:
            if (!options.fullYear) {
              s = String(s); s = s.substr(s.length - 2);
              break;
            }
            // fallthrough
          default:
            pad = true;
        }
        break;
      case 'Q':
      case 'q':
        s = Math.ceil((dateObject.getMonth() + 1) / 3);
        pad = true;
        break;
      case 'M':
      case 'L': {
        var m = dateObject.getMonth();
        if (l < 3) {
          s = m + 1; pad = true;
        } else {
          var propM = ['months', c == 'L' ? 'standAlone' : 'format', widthList[l - 3]].join('-');
          s = bundle[propM][m];
        }
        break;
      }
      case 'w': {
        var firstDay = 0;
        s = _getWeekOfYear(dateObject, firstDay); pad = true;
        break;
      }
      case 'd':
        s = dateObject.getDate(); pad = true;
        break;
      case 'D':
        s = _getDayOfYear(dateObject); pad = true;
        break;
      case 'e':
      case 'c': {
        var d = dateObject.getDay();
        if (l < 2) {
          s = (d - supplemental.getFirstDayOfWeek(options.locale) + 8) % 7;
          break;
        }
        // fallthrough
      }
      case 'E': {
        var dd = dateObject.getDay();
        if (l < 3) {
          s = dd + 1; pad = true;
        } else {
          var propD = ['days', c == 'c' ? 'standAlone' : 'format', widthList[l - 3]].join('-');
          s = bundle[propD][dd];
        }
        break;
      }
      case 'a': {
        var timePeriod = dateObject.getHours() < 12 ? 'am' : 'pm';
        s = options[timePeriod] || bundle['dayPeriods-format-wide-' + timePeriod];
        break;
      }
      case 'h':
      case 'H':
      case 'K':
      case 'k': {
        var h = dateObject.getHours();
        switch (c) {
          case 'h': s = (h % 12) || 12; break;
          case 'H': s = h; break;
          case 'K': s = (h % 12); break;
          case 'k': s = h || 24; break;
        }
        pad = true;
        break;
      }
      case 'm':
        s = dateObject.getMinutes(); pad = true;
        break;
      case 's':
        s = dateObject.getSeconds(); pad = true;
        break;
      case 'S':
        s = Math.round(dateObject.getMilliseconds() * Math.pow(10, l - 3)); pad = true;
        break;
      case 'v':
      case 'z':
        s = _getZone(dateObject, true, options);
        if (s) { break; }
        l = 4;
        // fallthrough... use GMT if tz not available
      case 'Z': {
        var offset = _getZone(dateObject, false, options);
        var tz = [
          (offset <= 0 ? '+' : '-'),
          string.pad(Math.floor(Math.abs(offset) / 60), 2),
          string.pad(Math.abs(offset) % 60, 2)
        ];
        if (l == 4) {
          tz.splice(0, 0, 'GMT');
          tz.splice(3, 0, ':');
        }
        s = tz.join('');
        break;
      }
      default:
        throw new Error('dojo.date.locale.format: invalid pattern char: ' + pattern);
    }
    if (pad) { s = string.pad(s, l); }
    return s;
  });
}

// ---------------------------------------------------------------------------
// _processPattern — faithful port. Split a pattern on single quotes (literals).
// ---------------------------------------------------------------------------
export function _processPattern(pattern: string, applyPattern?: any, applyLiteral?: any, applyAll?: any): string {
  var identity = function (x: any) { return x; };
  applyPattern = applyPattern || identity;
  applyLiteral = applyLiteral || identity;
  applyAll = applyAll || identity;

  var chunks: any = pattern.match(/(''|[^'])+/g),
    literal = pattern.charAt(0) == "'";

  array.forEach(chunks, function (chunk: any, i: number) {
    if (!chunk) {
      chunks[i] = '';
    } else {
      chunks[i] = (literal ? applyLiteral : applyPattern)(chunk.replace(/''/g, "'"));
      literal = !literal;
    }
  });
  return applyAll(chunks.join(''));
}

// ---------------------------------------------------------------------------
// _buildDateTimeRE — faithful port. Build the parse regexp for a literal-free pattern.
// ---------------------------------------------------------------------------
function _buildDateTimeRE(tokens: any, bundle: any, options: any, pattern: string): string {
  pattern = dregexp.escapeString(pattern);
  if (!options.strict) { pattern = pattern.replace(' a', ' ?a'); } // tolerate no space before am/pm
  return pattern.replace(/([a-z])\1*/ig, function (match: string) {
    var s: any,
      c = match.charAt(0),
      l = match.length,
      p2 = '', p3 = '';
    if (options.strict) {
      if (l > 1) { p2 = '0' + '{' + (l - 1) + '}'; }
      if (l > 2) { p3 = '0' + '{' + (l - 2) + '}'; }
    } else {
      p2 = '0?'; p3 = '0{0,2}';
    }
    switch (c) {
      case 'y':
        s = '\\d{2,4}';
        break;
      case 'M':
      case 'L':
        if (l > 2) {
          var months = bundle['months-' + (c == 'L' ? 'standAlone' : 'format') + '-' + _widthList[l - 3]].slice(0);
          s = months.join('|');
          if (!options.strict) {
            s = s.replace(/\./g, '');
            s = '(?:' + s + ')\\.?';
          }
        } else {
          s = '1[0-2]|' + p2 + '[1-9]';
        }
        break;
      case 'D':
        s = '[12][0-9][0-9]|3[0-5][0-9]|36[0-6]|' + p2 + '[1-9][0-9]|' + p3 + '[1-9]';
        break;
      case 'd':
        s = '3[01]|[12]\\d|' + p2 + '[1-9]';
        break;
      case 'w':
        s = '[1-4][0-9]|5[0-3]|' + p2 + '[1-9]';
        break;
      case 'E':
      case 'e':
      case 'c':
        s = '.+?';
        break;
      case 'h': //hour (1-12)
        s = '1[0-2]|' + p2 + '[1-9]';
        break;
      case 'k': //hour (0-11)
        s = '1[01]|' + p2 + '\\d';
        break;
      case 'H': //hour (0-23)
        s = '1\\d|2[0-3]|' + p2 + '\\d';
        break;
      case 'K': //hour (1-24)
        s = '1\\d|2[0-4]|' + p2 + '[1-9]';
        break;
      case 'm':
      case 's':
        s = '[0-5]\\d';
        break;
      case 'S':
        s = '\\d{' + l + '}';
        break;
      case 'a': {
        var am = options.am || bundle['dayPeriods-format-wide-am'],
          pm = options.pm || bundle['dayPeriods-format-wide-pm'];
        s = am + '|' + pm;
        if (!options.strict) {
          if (am != am.toLowerCase()) { s += '|' + am.toLowerCase(); }
          if (pm != pm.toLowerCase()) { s += '|' + pm.toLowerCase(); }
          if (s.indexOf('.') != -1) { s += '|' + s.replace(/\./g, ''); }
        }
        s = s.replace(/\./g, '\\.');
        break;
      }
      default:
        s = '.*';
    }

    if (tokens) { tokens.push(match); }

    return '(' + s + ')';
  }).replace(/[\xa0 ]/g, '[\\s\\xa0]'); // normalize whitespace
}

// ---------------------------------------------------------------------------
// Public format() — routes the CLDR-pattern path through the faithful engine while
// preserving the app's EXPLICIT `{datePattern[,timePattern]}` behavior (and the pinned
// date-locale.test): explicit patterns are date-then-time, space-joined (NOT combined
// via CLDR dateTimeFormat). Everything else follows faithful dojo.
// ---------------------------------------------------------------------------
export function format(dateObject: Date, options?: any): string {
  options = options || {};
  var locale = normalizeLocale(options.locale),
    bundle = _getGregorianBundle(locale),
    sauce = lang.hitch(null, formatPattern, dateObject, bundle, options);

  if (options.selector == 'year') {
    return _processPattern(bundle['dateFormatItem-yyyy'] || 'yyyy', sauce);
  }

  // EXPLICIT-pattern path (mds-preserved).
  if (options.datePattern || options.timePattern) {
    var chunks: string[] = [];
    if (options.selector != 'time' && options.datePattern) { chunks.push(_processPattern(options.datePattern, sauce)); }
    if (options.selector != 'date' && options.timePattern) { chunks.push(_processPattern(options.timePattern, sauce)); }
    if (chunks.length) { return chunks.join(' '); }
  }

  // Faithful formatLength-driven path (CLDR dateFormat / timeFormat / dateTimeFormat).
  var formatLength = options.formatLength || 'short',
    str: string[] = [],
    pattern;
  if (options.selector != 'date') {
    pattern = bundle['timeFormat-' + formatLength];
    if (pattern) { str.push(_processPattern(pattern, sauce)); }
  }
  if (options.selector != 'time') {
    pattern = bundle['dateFormat-' + formatLength];
    if (pattern) { str.push(_processPattern(pattern, sauce)); }
  }

  return str.length == 1 ? str[0] : bundle['dateTimeFormat-' + formatLength].replace(/\'/g, '').replace(/\{(\d+)\}/g,
    function (match: string, key: any) { return str[key]; });
}

// ---------------------------------------------------------------------------
// regexp / _parseInfo — faithful port. Build the localized parse regexp + token list.
// ---------------------------------------------------------------------------
export function regexp(options?: any): string {
  return _parseInfo(options).regexp;
}

export function _parseInfo(options?: any): any {
  options = options || {};
  var locale = normalizeLocale(options.locale),
    bundle = _getGregorianBundle(locale),
    formatLength = options.formatLength || 'short',
    datePattern = options.datePattern || bundle['dateFormat-' + formatLength],
    timePattern = options.timePattern || bundle['timeFormat-' + formatLength],
    pattern;
  if (options.selector == 'date') {
    pattern = datePattern;
  } else if (options.selector == 'time') {
    pattern = timePattern;
  } else {
    pattern = bundle['dateTimeFormat-' + formatLength].replace(/\{(\d+)\}/g,
      function (match: string, key: any) { return [timePattern, datePattern][key]; });
  }

  var tokens: any[] = [],
    re = _processPattern(pattern, lang.hitch(null, _buildDateTimeRE, tokens, bundle, options));
  return { regexp: re, tokens: tokens, bundle: bundle };
}

// ---------------------------------------------------------------------------
// Public parse() — faithful port.
// ---------------------------------------------------------------------------
export function parse(value: string, options?: any): Date | null {
  options = options || {};
  // remove non-printing bidi control chars from input and pattern
  var controlChars = /[‎‏‪‮]/g,
    info = _parseInfo(options),
    tokens = info.tokens, bundle = info.bundle,
    re = new RegExp('^' + info.regexp.replace(controlChars, '') + '$',
      options.strict ? '' : 'i'),
    match = re.exec(value && value.replace(controlChars, ''));

  if (!match) { return null; }

  var widthList = ['abbr', 'wide', 'narrow'],
    result: any[] = [1970, 0, 1, 0, 0, 0, 0], // will get converted to a Date at the end
    amPm = '',
    valid = array.every(match, function (v: any, i: number) {
      if (!i) { return true; }
      var token = tokens[i - 1],
        l = token.length,
        c = token.charAt(0);
      switch (c) {
        case 'y':
          if (l != 2 && options.strict) {
            result[0] = v;
          } else {
            if (v < 100) {
              v = Number(v);
              // sliding window: 80 years before, 20 after the present year
              var year = '' + new Date().getFullYear(),
                century = (year.substring(0, 2) as any) * 100,
                cutoff = Math.min(Number(year.substring(2, 4)) + 20, 99);
              result[0] = (v < cutoff) ? century + v : century - 100 + v;
            } else {
              if (options.strict) { return false; }
              result[0] = v;
            }
          }
          break;
        case 'M':
        case 'L':
          if (l > 2) {
            var months = bundle['months-' + (c == 'L' ? 'standAlone' : 'format') + '-' + widthList[l - 3]].concat();
            if (!options.strict) {
              v = v.replace('.', '').toLowerCase();
              months = array.map(months, function (s: any) { return s.replace('.', '').toLowerCase(); });
            }
            v = array.indexOf(months, v);
            if (v == -1) { return false; }
          } else {
            v--;
          }
          result[1] = v;
          break;
        case 'E':
        case 'e':
        case 'c': {
          var days = bundle['days-' + (c == 'c' ? 'standAlone' : 'format') + '-' + widthList[l - 3]].concat();
          if (!options.strict) {
            v = v.toLowerCase();
            days = array.map(days, function (d: any) { return d.toLowerCase(); });
          }
          v = array.indexOf(days, v);
          if (v == -1) { return false; }
          break;
        }
        case 'D':
          result[1] = 0;
          // fallthrough...
        case 'd':
          result[2] = v;
          break;
        case 'a': {
          var am = options.am || bundle['dayPeriods-format-wide-am'],
            pm = options.pm || bundle['dayPeriods-format-wide-pm'];
          if (!options.strict) {
            var period = /\./g;
            v = v.replace(period, '').toLowerCase();
            am = am.replace(period, '').toLowerCase();
            pm = pm.replace(period, '').toLowerCase();
          }
          if (options.strict && v != am && v != pm) { return false; }
          amPm = (v == pm) ? 'p' : (v == am) ? 'a' : '';
          break;
        }
        case 'K': // hour (1-24)
          if (v == 24) { v = 0; }
          // fallthrough...
        case 'h': // hour (1-12)
        case 'H': // hour (0-23)
        case 'k': // hour (0-11)
          if (v > 23) { return false; }
          result[3] = v;
          break;
        case 'm':
          result[4] = v;
          break;
        case 's':
          result[5] = v;
          break;
        case 'S':
          result[6] = v;
      }
      return true;
    });

  var hours = +result[3];
  if (amPm === 'p' && hours < 12) {
    result[3] = hours + 12; // e.g., 3pm -> 15
  } else if (amPm === 'a' && hours == 12) {
    result[3] = 0; // 12am -> 0
  }

  var dateObject = new Date(result[0], result[1], result[2], result[3], result[4], result[5], result[6]);
  if (options.strict) {
    dateObject.setFullYear(result[0]);
  }

  // Check for overflow.  The Date() constructor normalizes things like April 32nd...
  var allTokens = tokens.join(''),
    dateToken = allTokens.indexOf('d') != -1,
    monthToken = allTokens.indexOf('M') != -1;

  if (!valid ||
    (monthToken && dateObject.getMonth() > result[1]) ||
    (dateToken && dateObject.getDate() > result[2])) {
    return null;
  }

  // Check for underflow, due to DST shifts.  See #9366
  if ((monthToken && dateObject.getMonth() < result[1]) ||
    (dateToken && dateObject.getDate() < result[2])) {
    dateObject = ddate.add(dateObject, 'hour', 1);
  }

  return dateObject;
}

// ---------------------------------------------------------------------------
// getNames — UNCHANGED existing export (Intl-backed). Days Sunday-first (0=Sunday),
// months January-first (0=January), matching CalendarLite indexing and the CLDR order
// the format/parse engine above expects.
// ---------------------------------------------------------------------------
export function getNames(item: 'days' | 'months', type: 'wide' | 'abbr' | 'narrow',
  _context?: string, locale?: string, ..._rest: any[]): string[] {
  const loc = locale || resolveLocale({});
  const intlWidth = type === 'wide' ? 'long' : (type === 'abbr' ? 'short' : 'narrow');
  const out: string[] = [];
  if (item === 'months') {
    const fmt = new Intl.DateTimeFormat(loc, { month: intlWidth as any });
    for (let m = 0; m < 12; m++) { out.push(fmt.format(new Date(2021, m, 1))); }
  } else {
    // 2023-01-01 is a Sunday, so days 1..7 give Sun..Sat (CLDR day order).
    const fmt = new Intl.DateTimeFormat(loc, { weekday: intlWidth as any });
    for (let d = 0; d < 7; d++) { out.push(fmt.format(new Date(2023, 0, 1 + d))); }
  }
  return out;
}

// isWeekend — faithful port (dojo/date/locale.isWeekend), backed by cldr/supplemental.
export function isWeekend(dateObject?: Date, locale?: string): boolean {
  var weekend = supplemental.getWeekend(locale),
    day = (dateObject || new Date()).getDay();
  if (weekend.end < weekend.start) {
    weekend.end += 7;
    if (day < weekend.start) { day += 7; }
  }
  return day >= weekend.start && day <= weekend.end;
}

// _getDayOfYear / _getWeekOfYear — faithful ports (used by the 'D'/'w' format tokens).
export function _getDayOfYear(dateObject: Date): number {
  return ddate.difference(new Date(dateObject.getFullYear(), 0, 1, dateObject.getHours()), dateObject) + 1;
}

export function _getWeekOfYear(dateObject: Date, firstDayOfWeek?: number): number {
  if (arguments.length == 1) { firstDayOfWeek = 0; } // Sunday

  var firstDayOfYear = new Date(dateObject.getFullYear(), 0, 1).getDay(),
    adj = ((firstDayOfYear as any) - (firstDayOfWeek as any) + 7) % 7,
    week = Math.floor((_getDayOfYear(dateObject) + adj - 1) / 7);

  if (firstDayOfYear == firstDayOfWeek) { week++; }

  return week;
}

// Proxy to dojo/cldr/supplemental (kept on the locale module for convenience).
export function getFirstDayOfWeek(locale?: string): number {
  return supplemental.getFirstDayOfWeek(locale);
}

const locale = {
  format, parse, getNames, getFirstDayOfWeek, regexp, _parseInfo, _processPattern,
  _getGregorianBundle, addCustomFormats, isWeekend, _getWeekOfYear, _getDayOfYear, _getZone,
};
export default locale;
